import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera, Upload, Shield, Share2, Star, Search, Menu, X, LogOut, LayoutDashboard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { auth } from '../lib/api';
import { isAuthenticated, getStoredUser, dashboardPathForRole, clearSession } from '../lib/auth';

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  // Header starts fully transparent over the hero image, like jacadijo.com:
  // it turns solid white once the page is scrolled, or on hover (even at the top).
  const [scrolled, setScrolled] = React.useState(false);
  const [navHovered, setNavHovered] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const runSearch = () => {
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    navigate(params.toString() ? `/photographers?${params.toString()}` : '/photographers');
  };

  const loggedIn = isAuthenticated();
  const user = getStoredUser();

  const handleLogout = async () => {
    try {
      await auth.logout();
    } catch {
      // ignore - we're clearing the local session regardless
    }
    clearSession();
    navigate('/auth');
  };

  const navSolid = scrolled || navHovered || mobileMenuOpen;

  const categories = [
    {
      name: 'Weddings',
      url: 'https://images.unsplash.com/photo-1768445457231-3c042ea1e6ad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHx3ZWRkaW5nJTIwY291cGxlJTIwYXJ0aXN0aWN8ZW58MHx8fHwxNzcxMTM5NjUwfDA&ixlib=rb-4.1.0&q=85',
    },
    {
      name: 'Food',
      url: 'https://images.unsplash.com/photo-1761095596709-b513fc23b3f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxmb29kJTIwcGhvdG9ncmFwaHklMjBwbGF0ZWQlMjBhcnRpc3RpY3xlbnwwfHx8fDE3NzExMzk2NTF8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      name: 'Fashion',
      url: 'https://images.unsplash.com/photo-1685464670752-e4b571ea1464?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxmYXNoaW9uJTIwbW9kZWwlMjBzdHJlZXQlMjBzdHlsZXxlbnwwfHx8fDE3NzExMjg0ODF8MA&ixlib=rb-4.1.0&q=85',
    },
    {
      name: 'Corporate',
      url: 'https://images.unsplash.com/photo-1763739530672-4aadafbd81ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjBldmVudCUyMG1lZXRpbmclMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzcxMTM5NjUzfDA&ixlib=rb-4.1.0&q=85',
    },
  ];

  const features = [
    {
      icon: <Camera className="w-8 h-8" />,
      title: 'Find Top Photographers',
      description: 'Browse portfolios, read reviews, and discover the perfect photographer for your needs.',
    },
    {
      icon: <Upload className="w-8 h-8" />,
      title: 'Secure Asset Delivery',
      description: 'Receive your high-resolution photos and videos in a private, encrypted cloud vault.',
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Protected Storage',
      description: 'Your memories are safe with enterprise-grade security and automated backups.',
    },
    {
      icon: <Share2 className="w-8 h-8" />,
      title: 'Easy Sharing',
      description: 'Create password-protected galleries and share your favorite moments with anyone.',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 border-b ${
          navSolid ? 'bg-white/95 backdrop-blur-md border-border shadow-sm' : 'bg-transparent border-transparent'
        }`}
        onMouseEnter={() => setNavHovered(true)}
        onMouseLeave={() => setNavHovered(false)}
        data-testid="main-nav"
      >
        <div className="px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className={`flex items-center space-x-2 transition-colors ${navSolid ? 'text-foreground' : 'text-white'}`} data-testid="logo-link">
              <Camera className="w-8 h-8" />
              <span className="font-playfair text-2xl font-bold">Pixnest</span>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/photographers"
                className={`text-sm font-medium transition-colors ${navSolid ? 'text-foreground hover:text-primary' : 'text-white hover:text-white/70'}`}
                data-testid="nav-photographers"
              >
                Find Photographers
              </Link>
              <Link
                to="/photographer/dashboard"
                className={`text-sm font-medium transition-colors ${navSolid ? 'text-foreground hover:text-primary' : 'text-white hover:text-white/70'}`}
                data-testid="nav-photographer-dashboard"
              >
                For Photographers
              </Link>
              {loggedIn ? (
                <>
                  <Link to={dashboardPathForRole(user?.role)} data-testid="nav-dashboard">
                    <Button variant="ghost" size="sm" className={navSolid ? '' : 'text-white hover:bg-white/10 hover:text-white'}>
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Dashboard
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className={navSolid ? '' : 'text-white hover:bg-white/10 hover:text-white'}
                    data-testid="nav-logout"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Log Out
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/auth" data-testid="nav-signin">
                    <Button variant="ghost" size="sm" className={navSolid ? '' : 'text-white hover:bg-white/10 hover:text-white'}>Sign In</Button>
                  </Link>
                  <Link to="/auth" data-testid="nav-getstarted">
                    <Button size="sm">Get Started</Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className={`md:hidden transition-colors ${navSolid ? 'text-foreground' : 'text-white'}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden pt-4 pb-2 space-y-2" data-testid="mobile-menu">
              <Link to="/photographers" className="block py-2 text-sm font-medium" data-testid="mobile-nav-photographers">
                Find Photographers
              </Link>
              <Link to="/photographer/dashboard" className="block py-2 text-sm font-medium" data-testid="mobile-nav-photographer-dashboard">
                For Photographers
              </Link>
              <div className="flex flex-col space-y-2 pt-2">
                {loggedIn ? (
                  <>
                    <Link to={dashboardPathForRole(user?.role)} data-testid="mobile-nav-dashboard">
                      <Button variant="ghost" size="sm" className="w-full">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Dashboard
                      </Button>
                    </Link>
                    <Button variant="ghost" size="sm" className="w-full" onClick={handleLogout} data-testid="mobile-nav-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Log Out
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/auth" data-testid="mobile-nav-signin">
                      <Button variant="ghost" size="sm" className="w-full">Sign In</Button>
                    </Link>
                    <Link to="/auth" data-testid="mobile-nav-getstarted">
                      <Button size="sm" className="w-full">Get Started</Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20" data-testid="hero-section">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://images.unsplash.com/photo-1758607234944-3f423987893d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxwaG90b2dyYXBoZXIlMjBob2xkaW5nJTIwY2FtZXJhJTIwc3Vuc2V0fGVufDB8fHx8MTc3MTEzOTY0OXww&ixlib=rb-4.1.0&q=85')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative z-10 px-6 md:px-12 lg:px-24 text-center">
          <h1 className="font-playfair text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1] text-white mb-6" data-testid="hero-title">
            Capture Moments,
            <br />
            Deliver Memories
          </h1>
          <p className="text-lg md:text-xl leading-relaxed text-white/90 max-w-2xl mx-auto mb-12" data-testid="hero-description">
            India's all-in-one platform connecting photographers with clients, featuring secure asset delivery and cloud storage. Starting in Kolkata.
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="flex gap-2" data-testid="hero-search">
              <Input
                type="text"
                placeholder="Search photographers, city, or specialty (e.g. Kolkata)..."
                className="flex-1 bg-white/90 text-foreground"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runSearch()}
                data-testid="hero-search-input"
              />
              <Button size="lg" onClick={runSearch} data-testid="hero-search-button">
                <Search className="w-5 h-5 mr-2" />
                Search
              </Button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/photographers" data-testid="hero-browse-button">
              <Button size="lg" variant="default" className="bg-white text-foreground hover:bg-white/90">
                Browse Photographers
              </Button>
            </Link>
            <Link to="/auth" data-testid="hero-photographer-button">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                I'm a Photographer
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="categories-section">
        <div className="text-center mb-16">
          <h2 className="font-playfair text-4xl md:text-5xl font-semibold tracking-tight leading-tight mb-4" data-testid="categories-title">
            Photography for Every Occasion
          </h2>
          <p className="text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto" data-testid="categories-description">
            From intimate moments to grand celebrations, find specialized photographers for your needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {categories.map((category, index) => (
            <Link
              key={index}
              to={`/photographers?category=${category.name.toLowerCase()}`}
              className="group cursor-pointer"
              data-testid={`category-${category.name.toLowerCase()}`}
            >
              <div className="relative overflow-hidden rounded-sm aspect-[4/5] mb-4 image-hover-scale">
                <img
                  src={category.url}
                  alt={category.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-playfair text-2xl font-semibold text-white">{category.name}</h3>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-muted/30" data-testid="features-section">
        <div className="text-center mb-16">
          <h2 className="font-playfair text-4xl md:text-5xl font-semibold tracking-tight leading-tight mb-4" data-testid="features-title">
            Everything You Need in One Platform
          </h2>
          <p className="text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto" data-testid="features-description">
            From discovery to delivery, we've built the complete ecosystem for modern photography.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <Card key={index} className="text-center border-none" data-testid={`feature-card-${index}`}>
              <CardHeader>
                <div className="mx-auto mb-4 w-16 h-16 rounded-sm bg-primary/10 flex items-center justify-center text-primary">
                  {feature.icon}
                </div>
                <CardTitle className="text-xl">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">{feature.description}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="stats-section">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
          <div data-testid="stat-photographers">
            <div className="font-playfair text-5xl md:text-6xl font-bold mb-2">10,000+</div>
            <div className="text-lg text-muted-foreground">Professional Photographers</div>
          </div>
          <div data-testid="stat-bookings">
            <div className="font-playfair text-5xl md:text-6xl font-bold mb-2">50,000+</div>
            <div className="text-lg text-muted-foreground">Successful Bookings</div>
          </div>
          <div data-testid="stat-assets">
            <div className="font-playfair text-5xl md:text-6xl font-bold mb-2">5M+</div>
            <div className="text-lg text-muted-foreground">Assets Delivered</div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-primary text-primary-foreground" data-testid="cta-section">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="font-playfair text-4xl md:text-5xl font-semibold tracking-tight leading-tight mb-6" data-testid="cta-title">
            Ready to Get Started?
          </h2>
          <p className="text-lg md:text-xl leading-relaxed mb-12 opacity-90" data-testid="cta-description">
            Join thousands of photographers and clients who trust Pixnest for their creative work.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/auth" data-testid="cta-client-button">
              <Button size="lg" variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10">
                Sign Up as Client
              </Button>
            </Link>
            <Link to="/auth" data-testid="cta-photographer-button">
              <Button size="lg" className="bg-primary-foreground text-primary hover:bg-primary-foreground/90">
                Join as Photographer
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 lg:px-24 border-t" data-testid="footer">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <Camera className="w-6 h-6" />
              <span className="font-playfair text-xl font-bold">Pixnest</span>
            </div>
            <p className="text-sm text-muted-foreground">
              The complete platform for photographers and clients.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">For Clients</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/photographers" className="hover:text-foreground">Find Photographers</Link></li>
              <li><Link to="/client/dashboard" className="hover:text-foreground">My Bookings</Link></li>
              <li><Link to="/vault" className="hover:text-foreground">Asset Vault</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">For Photographers</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/photographer/dashboard" className="hover:text-foreground">Dashboard</Link></li>
              <li><Link to="/auth" className="hover:text-foreground">Create Profile</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="#" className="hover:text-foreground">About</a></li>
              <li><a href="#" className="hover:text-foreground">Contact</a></li>
              <li><a href="#" className="hover:text-foreground">Privacy</a></li>
              <li><a href="#" className="hover:text-foreground">Terms</a></li>
            </ul>
          </div>
        </div>
        <div className="pt-8 border-t text-center text-sm text-muted-foreground">
          <p>&copy; 2026 Pixnest. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;